import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  Card,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { getLoggedInUser, updateProfile } from "../services/UserService";
import UserData from "../models/UserData";
import { getProfileImageUrl, handleImageError } from "../utils/imageUtils";
import { apiPost } from "../services/BaseService";
import { IMAGE_BASE } from "../Config";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<UserData>({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    profileImage: "",
    phone: "",
    address: "",
    gender: 0,
  });

  const [validationErrors, setValidationErrors] = useState({
    firstName: "",
    lastName: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      setUser(await getLoggedInUser());
    };
    fetchUser();
  }, []);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = async () => {
    // Reset user data to original values
    const originalUser = await getLoggedInUser();
    setUser(originalUser);
    setValidationErrors({ firstName: "", lastName: "" });
    setIsEditing(false);
  };
  const handleInputChange = (field: string, value: any) => {
    setUser({
      ...user,
      [field]: value,
    });

    // Clear validation errors when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors({
        ...validationErrors,
        [field]: "",
      });
    }
  };

  const handleProfileImageChange = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedFile) return null;
    
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      
      const result = await apiPost('/image/upload/profileImage', uploadFormData);
      return `/profileImage/${result.fileName}`;
    } catch (error) {
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const errors = { firstName: "", lastName: "" };
    let isValid = true;

    if (!user.firstName?.trim()) {
      errors.firstName = "First name is required";
      isValid = false;
    }

    if (!user.lastName?.trim()) {
      errors.lastName = "Last name is required";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let imageFileName = user.profileImage;
      
      if (selectedFile) {
        imageFileName = (await uploadImage()) || '';
      }
      
      const profileData = {
        firstName: user.firstName,
        lastName: user.lastName,
        profileImage: imageFileName || '',
        address: user.address,
        gender: user.gender,
      };
      
      const updatedUser = await updateProfile(profileData);
      setUser({ ...user, ...updatedUser });
      setSelectedFile(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      // Handle error
    }
  };

  return (
    <PermissionWrapper pageName="ProfilePage">
      <Container className="mt-5">
        <Card>
          <Card.Body>
            <h2>Profile Details</h2>
            {isEditing ? (
              <>
                <Form onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="username">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          value={user.username || ""}
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={user.email || ""}
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="phone">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          value={user.phone || ""}
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="firstName">
                        <Form.Label>First Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={user.firstName || ""}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          isInvalid={!!validationErrors.firstName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.firstName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="lastName">
                        <Form.Label>Last Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={user.lastName || ""}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          isInvalid={!!validationErrors.lastName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.lastName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="gender">
                        <Form.Label>Gender</Form.Label>
                        <Form.Select
                          value={user.gender || 0}
                          onChange={(e) => handleInputChange('gender', parseInt(e.target.value))}
                        >
                          <option value={0}>Male</option>
                          <option value={1}>Female</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group controlId="address" className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={user.address || ""}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group controlId="profileImage" className="mb-3">
                    <Form.Label>Profile Image</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                    />
                    <Form.Text className="text-muted">
                      Upload a profile image (JPG, PNG, GIF, SVG)
                    </Form.Text>
                    {(user.profileImage || selectedFile) && (
                      <div className="mt-2">
                        <Image
                          src={selectedFile ? URL.createObjectURL(selectedFile) : getProfileImageUrl(user.profileImage)}
                          alt="Profile"
                          thumbnail
                          style={{ width: "150px", height: "150px", objectFit: "cover" }}
                          onError={(e) => handleImageError(e, 'USER')}
                        />
                      </div>
                    )}
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button variant="primary" type="submit" disabled={uploading}>
                      {uploading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Uploading...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                    <Button variant="secondary" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              </>
            ) : (
              <div>
                <Row className="mb-3">
                  <Col md={6}>
                    <p><strong>Username:</strong> {user.username || "N/A"}</p>
                    <p><strong>Email:</strong> {user.email || "N/A"}</p>
                    <p><strong>Phone:</strong> {user.phone || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>First Name:</strong> {user.firstName || "N/A"}</p>
                    <p><strong>Last Name:</strong> {user.lastName || "N/A"}</p>
                    <p><strong>Gender:</strong> {user.gender === 1 ? "Female" : "Male"}</p>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={8}>
                    <p><strong>Address:</strong> {user.address || "N/A"}</p>
                  </Col>
                  <Col md={4}>
                    <Image
                      src={getProfileImageUrl(user.profileImage)}
                      alt="Profile"
                      thumbnail
                      style={{ width: "150px", height: "150px", objectFit: "cover" }}
                      onError={(e) => handleImageError(e, 'USER')}
                    />
                  </Col>
                </Row>
                
                <Button variant="primary" onClick={handleEditClick}>
                  Edit Profile
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Customer Support Widget */}
        {/* <CustomerSupportWidget 
          position="bottom-right" 
          userName={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || ''}
          userPhone={user.phone || ''}
        /> */}
      </Container>
    </PermissionWrapper>
  );
};

export default ProfilePage;
